/** Evidence room: what's waiting to be checked in, what's on the shelves, what's out; scan a label; chain of custody. */
import { FundingBox } from '../ops/Funding.tsx';
import { useEffect, useRef, useState } from 'react';
import { DISPOSITION_LABEL, type DispositionDecision, CHECKOUT_PURPOSES, code128Svg, DESTROY_METHODS, EVIDENCE_ACTION_LABEL, EVIDENCE_STATUS_LABEL, type EvidenceAction } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { fmtDateTime, fmtStamp } from '../shared/format.ts';
import { Icon } from '../shared/icons.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, ErrorBox, PageHeader, Spinner, useAction, useLoad, useNarrow } from '../shared/ui.tsx';
import { evidenceBase, reportsBase } from './ReportWizard.tsx';

type Tab = 'pending' | 'room' | 'out' | 'closed' | 'mine' | 'all' | 'court';
const TONE: Record<string, 'neutral' | 'info' | 'warn' | 'ok' | 'bad'> = { pending: 'warn', in_custody: 'ok', checked_out: 'info', released: 'neutral', destroyed: 'neutral' };
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY: Record<Tab, [string, string]> = {
  pending: ['Nothing waiting to be checked in', 'Everything deputies collected is in the room.'],
  room: ['The room is empty', 'Items show up here once they’re checked in.'],
  out: ['Nothing is checked out', 'Items sent to the lab, court or a detective show up here with their due-back date.'],
  closed: ['Nothing released or destroyed yet', 'Released and destroyed items are kept here for the record.'],
  mine: ['You haven’t collected any evidence', 'Items you mark as evidence on your reports show up here.'],
  all: ['No evidence yet', 'Items marked as evidence on reports show up here.'],
  court: ['No court orders waiting', 'When a court decides what happens to an item (release, destroy, forfeit), it shows up here to carry out.'],
};

export function EvidencePage() {
  const { navigate } = useRouter();
  const narrow = useNarrow();
  // ?tab=mine (from the deputy's "My evidence") opens straight on that list.
  const [tab, setTab] = useState<Tab | null>(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return t && ['pending', 'room', 'out', 'closed', 'mine', 'court'].includes(t) ? (t as Tab) : null;
  });
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [scan, setScan] = useState('');
  const [scanErr, setScanErr] = useState<unknown>(null);
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/evidence?${tab ? `tab=${tab}` : ''}${query ? `&q=${encodeURIComponent(query)}` : ''}`), [tab, query]);
  const manage = r.data?.canManage;
  const cur: Tab = tab ?? (manage ? 'pending' : 'mine');
  const c = r.data?.counts ?? {};
  // Nothing waiting to be checked in? Open on what's in the room instead of an empty list.
  useEffect(() => {
    if (tab === null && !query && manage && r.data && !c.pending) setTab('room');
  }, [tab, query, manage, r.data, c.pending]);
  const tabs: [Tab, string, number | null, boolean][] = [
    ['pending', 'Waiting to check in', c.pending || null, true],
    ['room', 'In the room', null, true],
    ['out', `Checked out${c.overdue ? ` · ${c.overdue} overdue` : ''}`, c.out || null, true],
    ['court', `Court orders${c.orders ? ` · ${c.orders} to do` : ''}`, c.court || null, true],
    ['closed', 'Released / destroyed', null, true],
    ['mine', 'Collected by me', null, true],
  ];
  const items: any[] = r.data?.items ?? [];
  const isOverdue = (x: any) => x.status === 'checked_out' && x.due_back && String(x.due_back).slice(0, 10) < today();
  const status = (x: any) => (
    <>
      <Badge tone={isOverdue(x) ? 'bad' : TONE[x.status] ?? 'neutral'} dot>
        {isOverdue(x) ? 'Overdue' : EVIDENCE_STATUS_LABEL[x.status as keyof typeof EVIDENCE_STATUS_LABEL]}
      </Badge>
      {x.disposition && (
        <Badge tone={x.disposition.status === 'decided' ? 'warn' : 'info'}>
          {x.disposition.status === 'decided' ? `Court: ${DISPOSITION_LABEL[x.disposition.decision as DispositionDecision]}` : 'Court deciding'}
        </Badge>
      )}
    </>
  );
  return (
    <div className="stack rp">
      <PageHeader
        crumb="Property & evidence"
        title="Evidence room"
        actions={
          <form
            className="row gap-sm"
            onSubmit={(e) => {
              e.preventDefault();
              setScanErr(null);
              api
                .get<any>(`/api/evidence/by-number/${encodeURIComponent(scan.trim())}`)
                .then((x) => navigate(`${evidenceBase()}/${x.item.id}`))
                .catch(setScanErr);
            }}
          >
            <input className="ev-scan mono" value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan label or type E26-000123" aria-label="Scan an evidence label" autoFocus />
            <button className="btn primary">Open</button>
          </form>
        }
      />
      <ErrorBox error={scanErr} />
      <div className="filterbar">
        <input className="rp-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, description, case #, shelf, who has it" aria-label="Search evidence" />
        <span className="spacer" />
        {r.data && <span className="muted small">{items.length} item{items.length === 1 ? '' : 's'}</span>}
      </div>
      <div className="tabs" role="tablist">
        {tabs.map(([k, l, n]) => (
          <button key={k} role="tab" aria-selected={cur === k} className={cur === k ? 'active' : ''} onClick={() => setTab(k)}>
            {l} {n ? <span className="rp-n">{n}</span> : null}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="card empty-state">
          <Icon name={query ? 'search' : 'check'} size={26} />
          <strong>{query ? 'No evidence matches that search' : EMPTY[cur][0]}</strong>
          <span className="muted">{query ? 'Try an item number, case number, shelf or name.' : EMPTY[cur][1]}</span>
        </div>
      ) : (
        <>
          {!narrow && (<div className="card flush rp-table-wrap">
            <div className="table-wrap">
              <table className="table rp-table ev-table">
                <thead>
                  <tr>
                    <th>Item #</th>
                    <th>Description</th>
                    <th>Case #</th>
                    <th>Status</th>
                    <th>Location / holder</th>
                    <th>Collected by</th>
                    <th>Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((x) => (
                    <tr key={x.id} className="clickable" onClick={() => navigate(`${evidenceBase()}/${x.id}`)}>
                      <td className="mono">
                        <button type="button" className="btn link rp-open ev-num" onClick={(e) => (e.stopPropagation(), navigate(`${evidenceBase()}/${x.id}`))}>
                          {x.number}
                        </button>
                      </td>
                      <td className="wrap">{x.description}</td>
                      <td className="mono">{x.case_number}</td>
                      <td>{status(x)}</td>
                      <td>{x.location || x.holder || <span className="muted">—</span>}</td>
                      <td>{x.collected_by_name}</td>
                      <td>{fmtStamp(x.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
)}
          {narrow && (<ul className="rp-list rp-cards">
            {items.map((x) => (
              <li key={x.id}>
                <button onClick={() => navigate(`${evidenceBase()}/${x.id}`)}>
                  <div className="rp-main">
                    <strong className="mono">{x.number}</strong>
                    <span>{x.description}</span>
                    <span className="small muted">
                      Case {x.case_number} · {x.collected_by_name} · {fmtStamp(x.created_at)}
                    </span>
                  </div>
                  <div className="rp-side">
                    {status(x)}
                    <span className="small">{x.location || x.holder || ''}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>)}
        </>
      )}
    </div>
  );
}

const FIELDS: Record<EvidenceAction, ('location' | 'person' | 'purpose' | 'dueBack' | 'method' | 'witness' | 'note')[]> = {
  check_in: ['location', 'note'],
  move: ['location', 'note'],
  check_out: ['person', 'purpose', 'dueBack', 'note'],
  return: ['location', 'note'],
  release: ['person', 'note'],
  destroy: ['method', 'witness', 'note'],
  note: ['note'],
};
const FIELD_LABEL = {
  location: 'Where (room, shelf, bin, locker)',
  person: 'Who',
  purpose: 'What for',
  dueBack: 'Due back',
  method: 'How it was destroyed',
  witness: 'Witness',
  note: 'Note',
};

export function EvidenceItem({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/evidence/${id}`), [id]);
  const [action, setAction] = useState<EvidenceAction | null>(null);
  const [f, setF] = useState<Record<string, string>>({});
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const { item, events, canManage, actions } = r.data;
  // Checking back in: suggest the shelf it came from.
  const lastPlace = item.location ?? [...events].reverse().find((e: any) => e.location)?.location ?? '';
  const court = r.data.court;
  const order = court?.status === 'decided' ? `Court order: ${DISPOSITION_LABEL[court.decision as DispositionDecision]} — ${court.decided_by_name}${court.court_case_number ? `, ${court.court_case_number}` : ''}, ${fmtDateTime(court.decided_at)}` : '';
  const choose = (x: EvidenceAction) => (setAction(x), setF(x === 'return' || x === 'move' ? { location: lastPlace } : x === 'destroy' ? { note: order } : x === 'release' && order ? { note: order } : {}));
  return (
    <div className="stack rw">
      <div className="rw-top">
        <div>
          <button className="btn link" onClick={() => navigate(evidenceBase())}>
            ← Evidence
          </button>
          <h1 className="mono">{item.number}</h1>
          <div>{item.description}</div>
          <button className="btn link small" onClick={() => navigate(`${reportsBase()}/${item.incident_id}`)}>
            Case {item.case_number}
          </button>
        </div>
        <div className="rw-state">
          <Badge tone={TONE[item.status] ?? 'neutral'}>{EVIDENCE_STATUS_LABEL[item.status as keyof typeof EVIDENCE_STATUS_LABEL]}</Badge>
          <button className="btn sm" onClick={() => window.open(`${evidenceBase()}/labels?item=${item.id}`, '_blank')}>
            Print label
          </button>
        </div>
      </div>
      <FundingBox kind="evidence" id={item.id} funding={item.funding} refText={item.funding_ref} canEdit={!!canManage} onSaved={r.reload} sold={item.status === 'released' || item.status === 'destroyed'} />
      {court && (
        <div className={`alert ${court.status === 'decided' ? 'warn' : 'info'}`}>
          {court.status === 'decided' ? (
            <>
              <strong>Court order: {DISPOSITION_LABEL[court.decision as DispositionDecision]}.</strong> {court.order_note} <span className="small">({court.decided_by_name}, {fmtDateTime(court.decided_at)})</span>
              {canManage && <div className="small">{court.decision === 'destroy' ? 'Use “Destroy” below — the order fills in the authorization.' : 'Use “Release” below to carry it out.'}</div>}
            </>
          ) : (
            <>
              <strong>Waiting on the court:</strong> this item’s case is on the court calendar{court.court_name ? ` (${court.court_name}, ${fmtDateTime(court.starts_at)})` : ''}. The court will say whether to keep, release, destroy or forfeit it.
            </>
          )}
        </div>
      )}
      <section className="rw-card">
        <header>
          <h3>Photos of this item ({(r.data.photos ?? []).length})</h3>
          <button className="btn sm" onClick={() => navigate(`${location.pathname.startsWith('/field') ? '/field' : ''}/photos?case=${item.incident_id}&evidence=${item.id}`)}>
            Add photos
          </button>
        </header>
        {(r.data.photos ?? []).length ? (
          <div className="ev-photos">
            {r.data.photos.map((ph: any) => (
              <a key={ph.id} href={`/api/reports/photos/${ph.id}`} target="_blank" rel="noreferrer">
                <img src={`/api/reports/photos/${ph.id}`} alt={ph.caption ?? item.number} />
              </a>
            ))}
          </div>
        ) : (
          <p className="small muted">No photos yet. Photos labeled with this item on its case show up here.</p>
        )}
      </section>
      <section className="rw-card">
        <dl className="kv">
          <dt>Where</dt>
          <dd>{item.status === 'checked_out' ? `Out with ${item.holder} — ${item.purpose}${item.due_back ? `, due back ${item.due_back}` : ''}` : item.location || (item.status === 'pending' ? `With ${item.collected_by_name} (not checked in yet)` : '—')}</dd>
          <dt>Collected</dt>
          <dd>
            {item.collected_by_name}, {fmtDateTime(item.created_at)}
          </dd>
        </dl>
      </section>
      {canManage && actions.length > 0 && (
        <section className="rw-card">
          <header>
            <h3>What’s happening with it?</h3>
          </header>
          <div className="rw-choices big">
            {(actions as EvidenceAction[]).map((x) => (
              <button key={x} type="button" className={action === x ? 'on' : ''} onClick={() => choose(x)}>
                {x === 'check_in' ? 'Check in' : x === 'move' ? 'Move' : x === 'check_out' ? 'Check out' : x === 'return' ? 'Check back in' : x === 'release' ? 'Release to owner' : x === 'destroy' ? 'Destroy' : 'Add a note'}
              </button>
            ))}
          </div>
          {action && (
            <form
              className="stack sm"
              onSubmit={(e) => {
                e.preventDefault();
                a.run(async () => {
                  await api.post(`/api/evidence/${id}/act`, { action, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v.trim())) });
                  setAction(null);
                  setF({});
                  r.reload();
                });
              }}
            >
              <div className="rw-row">
                {FIELDS[action].map((k) =>
                  k === 'purpose' ? (
                    <label key={k} className="rw-f">
                      <span>{FIELD_LABEL[k]}</span>
                      <select value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
                        <option value="">Choose…</option>
                        {CHECKOUT_PURPOSES.map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </label>
                  ) : k === 'method' ? (
                    <label key={k} className="rw-f">
                      <span>{FIELD_LABEL[k]}</span>
                      <select value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
                        <option value="">Choose…</option>
                        {DESTROY_METHODS.map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label key={k} className={`rw-f${k === 'note' || k === 'location' ? ' wide' : ''}`}>
                      <span>{k === 'note' && action === 'destroy' ? 'Authorized by (court order, solicitor, supervisor)' : k === 'person' && action === 'release' ? 'Released to (name and ID shown)' : FIELD_LABEL[k]}</span>
                      <input type={k === 'dueBack' ? 'date' : 'text'} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
                    </label>
                  ),
                )}
              </div>
              <div className="row gap">
                <button className="btn primary" disabled={a.busy}>
                  Save — {EVIDENCE_ACTION_LABEL[action]}
                </button>
                <button type="button" className="btn ghost" onClick={() => setAction(null)}>
                  Cancel
                </button>
              </div>
              <ErrorBox error={a.error} />
            </form>
          )}
        </section>
      )}
      <section className="rw-card">
        <header>
          <h3>Chain of custody</h3>
        </header>
        <ol className="ev-chain">
          {events.map((e: any, i: number) => (
            <li key={i}>
              <div>
                <strong>{EVIDENCE_ACTION_LABEL[e.action as keyof typeof EVIDENCE_ACTION_LABEL] ?? e.action}</strong> <span className="small muted">by {e.by_name}, {fmtDateTime(e.at)}</span>
              </div>
              <div className="small">
                {[
                  e.location && `→ ${e.location}`,
                  e.person && (e.action === 'check_out' ? `to ${e.person}` : e.action === 'release' ? `released to ${e.person}` : e.person),
                  e.purpose && `for ${e.purpose}`,
                  e.due_back && `due back ${e.due_back}`,
                  e.method && `method: ${e.method}`,
                  e.witness && `witness: ${e.witness}`,
                  e.note,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </li>
          ))}
        </ol>
        <p className="small muted">This record can’t be edited or deleted.</p>
      </section>
    </div>
  );
}

/** Printable labels: one item (?item=) or every item on a report (?incident=). */
export function EvidenceLabels() {
  const qs = new URLSearchParams(location.search);
  const item = qs.get('item');
  const incident = qs.get('incident');
  const r = useLoad(async () => {
    if (item) return [(await api.get<any>(`/api/evidence/${item}`)).item];
    const rep = await api.get<any>(`/api/reports/${incident}`);
    return Promise.all(rep.evidence.map((e: any) => api.get<any>(`/api/evidence/${e.id}`).then((x) => x.item)));
  }, [item, incident]);
  const printed = useRef(false);
  useEffect(() => {
    if (r.data?.length && !printed.current) {
      printed.current = true;
      setTimeout(() => window.print(), 300);
    }
  }, [r.data]);
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  return (
    <div className="ev-labels">
      {r.data.map((x: any) => (
        <div key={x.id} className="ev-label">
          <div className="ev-l-top">
            <strong>EVIDENCE</strong> <span>Case {x.case_number}</span>
          </div>
          <div className="ev-bar" dangerouslySetInnerHTML={{ __html: code128Svg(x.number, { moduleWidth: 2, height: 46 }) }} />
          <div className="ev-l-desc">{x.description}</div>
          <div className="ev-l-meta">
            Collected by {x.collected_by_name} · {fmtDateTime(x.created_at)}
          </div>
        </div>
      ))}
    </div>
  );
}
